import classNames from 'classnames';
import { createContext, useCallback, useContext, useEffect, useRef } from 'react';

const DropdownContext = createContext<{ close: () => void } | null>(null);

export const useDropdownClose = () => {
  const context = useContext(DropdownContext);
  if (!context) throw new Error('useDropdownClose must be used within a Dropdown component');
  return context.close;
};

export type DropdownProps = {
  trigger: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  dropdownClassName?: string;
  position?: 'dropdown-top' | 'dropdown-bottom' | 'dropdown-left' | 'dropdown-right' | 'dropdown-end';
  align?: 'dropdown-hover' | 'dropdown-open';
};

/**
 * DaisyUI dropdown component using method 1 (details/summary) with click-outside-to-close behavior
 */
export const Dropdown = (props: DropdownProps) => {
  const dropdownRef = useRef<HTMLDetailsElement>(null);

  const close = useCallback(() => {
    dropdownRef.current?.removeAttribute('open');
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current?.hasAttribute('open') && !dropdownRef.current.contains(event.target as Node)) {
        close();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [close]);

  return (
    <DropdownContext.Provider value={{ close }}>
      <details ref={dropdownRef} className={classNames('dropdown', props.position, props.align, props.className)}>
        <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">{props.trigger}</summary>
        <div className={classNames('dropdown-content', props.dropdownClassName)}>{props.children}</div>
      </details>
    </DropdownContext.Provider>
  );
};
